import React from 'react'
import { Typography } from '@material-ui/core'
import LaunchIcon from '@material-ui/icons/Launch'
import { useTranslation } from 'react-i18next'
import {
  DeliveryType,
  DonateDetailsType,
} from '../donate-details/donate-details.constants'
import {
  currencyTransform,
  CurrencyList,
  Locale,
} from '../../../utils/currency-transform'
import { useDonatePublishedSummaryStyles } from './donate-published-summary.styles'
import { hasNonprofitWebsite } from '../donate.utils'

export type DonatePublishedSummaryProps = {
  donateDetails: Partial<DonateDetailsType>
  nonprofitName?: string
  nonprofitWebsite?: string
  isDraft?: boolean
}

const getDeliveryLabelKey = (delivery?: string): string | null => {
  switch (delivery) {
    case DeliveryType.Shipping:
      return 'donation.publishedSummary.deliveryShipping'
    case DeliveryType.LocalPickup:
      return 'donation.publishedSummary.deliveryLocalPickup'
    case DeliveryType.Both:
      return 'donation.publishedSummary.deliveryBoth'
    default:
      return null
  }
}

export const DonatePublishedSummary: React.FC<DonatePublishedSummaryProps> = ({
  donateDetails,
  nonprofitName,
  nonprofitWebsite,
  isDraft = false,
}) => {
  const classes = useDonatePublishedSummaryStyles()
  const { t } = useTranslation()

  const conditionLabel =
    donateDetails.condition === 'new'
      ? t('donation.new')
      : donateDetails.condition === 'used'
        ? t('donation.used')
        : null

  const deliveryLabelKey = getDeliveryLabelKey(donateDetails.delivery)

  return (
    <div className={classes.root}>
      {!isDraft && (
        <img
          className={classes.thanksImage}
          src="/images/thanks.svg"
          alt=""
          aria-hidden
        />
      )}
      <div className={classes.content}>
        <span
          className={`${classes.statusBadge} ${
            isDraft ? classes.statusBadgeDraft : ''
          }`}
        >
          {isDraft
            ? t('donation.publishedSummary.draftStatus')
            : t('donation.publishedSummary.publishedStatus')}
        </span>

        {donateDetails.title && (
          <Typography variant="subtitle1" component="h2" className={classes.title}>
            {donateDetails.title}
          </Typography>
        )}

        {typeof donateDetails.price === 'number' && (
          <Typography className={classes.price}>
            {currencyTransform(donateDetails.price, Locale.US, {
              style: 'currency',
              currency: CurrencyList.USD,
            })}
          </Typography>
        )}

        <div className={classes.metaRow}>
          {conditionLabel && (
            <span className={classes.metaChip}>
              {t('donation.condition')}: {conditionLabel}
            </span>
          )}
          {deliveryLabelKey && (
            <span className={classes.metaChip}>{t(deliveryLabelKey)}</span>
          )}
        </div>

        {donateDetails.description && (
          <Typography className={classes.description}>
            {donateDetails.description}
          </Typography>
        )}
      </div>

      {nonprofitName && hasNonprofitWebsite(nonprofitWebsite) && (
        <div className={classes.nonprofitLinkRow}>
          <a
            href={nonprofitWebsite ?? ''}
            target="_blank"
            rel="noreferrer"
            className={classes.nonprofitLink}
          >
            {nonprofitName}
            <LaunchIcon />
          </a>
        </div>
      )}
    </div>
  )
}
